import { useCallback, useEffect, useState } from "react";
import { FileSort, FileSortKey, nextSort } from "../utils/fileSort";

export type FileSortState<S extends FileSort | null = FileSort | null> = {
  /** The active column + direction; `null` while nothing is sorted. */
  sort: S;
  /** Header/menu click: flips the active column, or switches to a new one. */
  onSort: (key: FileSortKey) => void;
};

export type FileSortOptions = {
  /**
   * localStorage key to remember the chosen sort under. Omit to keep the sort in
   * memory for this page view only: a share-link is a one-shot handover to a
   * recipient who is not our user and will not come back, so there is no
   * preference worth remembering, and a single key would reorder every other
   * share-link the same browser opens.
   */
  storageKey?: string;
  /**
   * Sort applied before the user picks a column. Omit to start from the order
   * the listing arrived in, which keeps one consistent default (grid and list,
   * first visit and every visit), does no sort work on first render, and leaves
   * the sender's order intact. A seeded default must name a column that is
   * always displayed: `size` and `modified` hide below 519px / 479px, where the
   * order would have no visible control to explain it.
   */
  initial?: FileSort | null;
};

/** Every key a persisted sort may name; anything else is treated as corrupt. */
const SORT_KEYS: FileSortKey[] = ["type", "name", "size", "modified"];

/**
 * The sort stored under `storageKey`, or `fallback` when it is absent, corrupt,
 * or localStorage is unavailable (private mode).
 */
const readPersistedSort = (
  storageKey: string | undefined,
  fallback: FileSort | null
): FileSort | null => {
  if (!storageKey) return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<FileSort>;
      if (
        SORT_KEYS.includes(parsed.key as FileSortKey) &&
        (parsed.direction === "asc" || parsed.direction === "desc")
      ) {
        return { key: parsed.key as FileSortKey, direction: parsed.direction };
      }
    }
  } catch {
    // ignore corrupt / unavailable storage
  }
  return fallback;
};

/** Seeded with a sort, so the caller never sees `null`. */
export function useFileSort(
  options: FileSortOptions & { initial: FileSort }
): FileSortState<FileSort>;
/** Unsorted until the user clicks a header. */
export function useFileSort(options?: FileSortOptions): FileSortState;

/**
 * The list view's column sort.
 *
 * Owns the state and the header-click reducer ({@link nextSort}), so every file
 * browser (share-link viewer, published space) sorts identically. Feed `sort` to
 * {@link FileListHeader} together with `onSort`, and to {@link compareEntries}
 * for the display order.
 *
 * Both the starting sort and whether it outlives the page view are the caller's
 * choice (see {@link FileSortOptions}): the published space remembers a user's
 * sort under its own key, the share-link viewer starts from the delivered order
 * and forgets the sort when the page closes.
 */
export function useFileSort({
  storageKey,
  initial = null,
}: FileSortOptions = {}): FileSortState {
  const [sort, setSort] = useState<FileSort | null>(() =>
    readPersistedSort(storageKey, initial)
  );

  const onSort = useCallback(
    (key: FileSortKey) => setSort((current) => nextSort(current, key)),
    []
  );

  useEffect(() => {
    if (!storageKey || !sort) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(sort));
    } catch {
      // Ignore persistence failures; the in-memory sort still applies.
    }
  }, [storageKey, sort]);

  return { sort, onSort };
}
